import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'goober';

import {
  MAX_EXPLORE_ROUNDS,
  type GenAiRequestHistoryRound,
} from '@/api/v4/genai-request-history.api';
import { useNavigateTo } from '@/routes/hooks/use-navigate-to';
import type { IOption } from '@/types';
import { Button } from '@/ui/button/button';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { Icon } from '@/ui/icons/icon';
import { Inline, Stack } from '@/ui/line/line';

import type { ExampleCompany } from '../explore.state';
import {
  getRoundFilterDeltas,
  isFilterOnlyRound,
  type ExploreRoundFilterDelta,
} from '../refine/explore-round-display';
import { useSubmitExploreRound } from '../refine/use-submit-explore-round';
import { isRegeneratePanelOpenState } from '../regenerate/regenerate-explore.state';
import { useSetAtom } from 'jotai';

export const ExploreRoundChainPane = ({
  inputData,
  latestRoundNumber,
  onBackToHistory,
  onViewingRoundChange,
  requestId,
  roundCount,
  rounds,
  searchTitle,
  viewingRoundNumber,
}: {
  inputData: {
    description: string;
    exampleCompanies: ExampleCompany[];
    locations: IOption[];
  };
  latestRoundNumber: number;
  onBackToHistory: () => void;
  onViewingRoundChange: (roundNumber: number) => void;
  requestId: string;
  roundCount: number;
  rounds: GenAiRequestHistoryRound[];
  searchTitle?: string;
  viewingRoundNumber: number;
}) => {
  const { t } = useTranslation('explore');
  const navigateTo = useNavigateTo();
  const setRegeneratePanelOpen = useSetAtom(isRegeneratePanelOpenState);
  const [draft, setDraft] = useState('');
  const { isSubmitting, submitWithTruncateGuard } = useSubmitExploreRound({
    latestRoundNumber,
    requestId,
    viewingRoundNumber,
  });

  const sortedRounds = useMemo(
    () =>
      [...rounds].sort((left, right) => left.roundNumber - right.roundNumber),
    [rounds],
  );

  const isRoundCapReached = viewingRoundNumber >= MAX_EXPLORE_ROUNDS;
  const roundsBehind = latestRoundNumber - viewingRoundNumber;
  const canSend =
    draft.trim().length > 0 && !isSubmitting && !isRoundCapReached;
  const referenceLabels = inputData.exampleCompanies
    .map(company => company.name)
    .filter(Boolean);

  const formatDelta = (delta: ExploreRoundFilterDelta) =>
    t(delta.kind, { items: delta.items.join(', ') });

  const handleSend = async () => {
    if (!canSend) {
      return;
    }

    const nextPrompt = draft.trim();
    await submitWithTruncateGuard({
      defaultDescription: inputData.description,
      defaultExampleCompanies: inputData.exampleCompanies,
      defaultLocations: inputData.locations,
      onBeforeSubmit: () => setDraft(''),
      updatedDescription: nextPrompt,
      updatedExampleCompanies: inputData.exampleCompanies,
      updatedLocations: inputData.locations,
    });
  };

  return (
    <Container direction="column">
      <ChainHead gap="8px">
        <BackButton type="button" onClick={onBackToHistory}>
          <Icon
            icon="ArrowLeft"
            width="12px"
            height="12px"
            color="#3c59f6"
            centered={false}
          />
          {t`allSearches`}
        </BackButton>
        {searchTitle ? <ChainTitle>{searchTitle}</ChainTitle> : null}
        <ChainSub>
          {t('roundOf', {
            current: viewingRoundNumber,
            total: roundCount,
          })}
        </ChainSub>
      </ChainHead>

      <ChainScroll>
        {sortedRounds.map((round, index) => {
          const isActive = round.roundNumber === viewingRoundNumber;
          const isStale = round.roundNumber > viewingRoundNumber;
          const filterOnly = isFilterOnlyRound({
            roundIndex: index,
            rounds: sortedRounds,
          });
          const previous = sortedRounds[index - 1];
          const deltas =
            filterOnly && previous
              ? getRoundFilterDeltas({ current: round, previous })
              : [];

          return (
            <RoundButton
              key={round.roundNumber}
              type="button"
              $active={isActive}
              $stale={isStale}
              onClick={() => {
                setDraft('');
                onViewingRoundChange(round.roundNumber);
              }}
            >
              <RoundNumber $active={isActive}>{round.roundNumber}</RoundNumber>
              <RoundBody gap="3px">
                {filterOnly ? (
                  <DeltaText $active={isActive} $stale={isStale}>
                    <Icon
                      icon="Filter"
                      width="11px"
                      height="11px"
                      color={isActive ? '#3c59f6' : '#9499B6'}
                      centered={false}
                    />
                    <span>{deltas.map(formatDelta).join(' · ')}</span>
                  </DeltaText>
                ) : (
                  <RoundText $active={isActive} $stale={isStale}>
                    {round.prompt}
                  </RoundText>
                )}
                <RoundMeta>
                  {filterOnly
                    ? t('companiesMetaFilters', { count: round.resultCount })
                    : t('companiesMeta', { count: round.resultCount })}
                </RoundMeta>
              </RoundBody>
            </RoundButton>
          );
        })}
      </ChainScroll>

      {roundsBehind > 0 ? (
        <ReplaceNote>
          {roundsBehind === 1
            ? t('truncateReplaceNote', {
                count: 1,
                from: viewingRoundNumber + 1,
              })
            : t('truncateReplaceNote', {
                count: roundsBehind,
                from: viewingRoundNumber + 1,
                to: latestRoundNumber,
              })}
        </ReplaceNote>
      ) : null}

      <ChainFoot gap="10px">
        <FiltLabel>{t`filtersForThisRound`}</FiltLabel>
        <Chip
          type="button"
          disabled={isRoundCapReached || isSubmitting}
          onClick={() => setRegeneratePanelOpen(true)}
        >
          <ChipLabel>{t`location`}</ChipLabel>
          <ChipValue>
            {inputData.locations.length
              ? inputData.locations.map(location => location.label).join(', ')
              : t`anyLocation`}
          </ChipValue>
          <Icon icon="Pen" width="11px" height="11px" color="#9499B6" />
        </Chip>
        <Chip
          type="button"
          disabled={isRoundCapReached || isSubmitting}
          onClick={() => setRegeneratePanelOpen(true)}
        >
          <ChipLabel>{t`refs`}</ChipLabel>
          <ChipValue>
            {referenceLabels.length ? referenceLabels.join(', ') : t`noneRefs`}
          </ChipValue>
          <Icon icon="Pen" width="11px" height="11px" color="#9499B6" />
        </Chip>

        {isRoundCapReached ? (
          <Capped gap="10px">
            <CappedHead>
              <Icon icon="Alert" width="13px" height="13px" color="#ffae63" />
              <span>
                {t('roundCapBlockedShort', { max: MAX_EXPLORE_ROUNDS })}
              </span>
            </CappedHead>
            <CappedText>{t`roundCapCarryOver`}</CappedText>
            <Button
              onClick={() => navigateTo.explore()}
            >{t`startNewSearch`}</Button>
          </Capped>
        ) : (
          <>
            <InputWrap>
              <RefineTextarea
                value={draft}
                placeholder={t`refinePlaceholder`}
                disabled={isSubmitting}
                onChange={event => setDraft(event.target.value)}
                onKeyDown={event => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    void handleSend();
                  }
                }}
              />
              <SendButton
                type="button"
                $active={canSend}
                disabled={!canSend}
                onClick={() => {
                  void handleSend();
                }}
                aria-label={t`refine`}
              >
                <Icon
                  icon="ArrowUp"
                  width="13px"
                  height="13px"
                  color="#ffffff"
                />
              </SendButton>
            </InputWrap>
            <CapCount>
              {t('roundsUsed', {
                max: MAX_EXPLORE_ROUNDS,
                used: roundCount,
              })}
            </CapCount>
          </>
        )}
      </ChainFoot>
    </Container>
  );
};

const Container = styled(Flexbox)`
  height: 100%;
  min-height: 0;
  width: 100%;
  min-width: 0;
  overflow: hidden;
`;

const ChainHead = styled(Stack)`
  flex-shrink: 0;
  padding: 16px 14px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray.c2};
`;

const BackButton = styled('button')`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  ${({ theme }) => theme.typography.acrossPlatform.tiny};
  color: ${({ theme }) => theme.colors.blue.primaryA};
  font-weight: 500;
`;

const ChainTitle = styled('div')`
  ${({ theme }) => theme.typography.acrossPlatform.small};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.basics.black};
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const ChainSub = styled('div')`
  font-size: 10.5px;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.gray.c10};
`;

const ChainScroll = styled('div')`
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  gap: 2px;
  min-height: 0;
  overflow-y: auto;
  padding: 10px 10px 4px;
  ${({ theme }) => theme.mixins.scrollbar};
`;

const RoundButton = styled('button')<{ $active: boolean; $stale: boolean }>`
  box-sizing: border-box;
  display: flex;
  gap: 8px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  text-align: left;
  padding: 8px 9px;
  border-radius: 8px;
  border: none;
  border-left: 3px solid
    ${({ $active, theme }) =>
      $active ? theme.colors.blue.primaryA : 'transparent'};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.blue.c8 : 'transparent'};
  cursor: pointer;
  opacity: ${({ $stale }) => ($stale ? 0.42 : 1)};

  &:hover {
    background: ${({ $active, theme }) =>
      $active ? theme.colors.blue.c8 : theme.colors.gray.c1};
  }
`;

const RoundNumber = styled('span')<{ $active: boolean }>`
  flex: 0 0 19px;
  height: 19px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
  font-size: 10px;
  font-weight: 600;
  background: ${({ $active, theme }) =>
    $active ? theme.colors.blue.primaryA : theme.colors.gray.c2};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.basics.white : theme.colors.gray.c12};
`;

const RoundBody = styled(Stack)`
  flex: 1;
  min-width: 0;
`;

const RoundText = styled('span')<{ $active: boolean; $stale: boolean }>`
  font-size: 11.5px;
  line-height: 1.4;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.basics.black : theme.colors.gray.c12};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-decoration: ${({ $stale }) => ($stale ? 'line-through' : 'none')};
  text-decoration-thickness: 1px;
`;

const DeltaText = styled('span')<{ $active: boolean; $stale: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 5px;
  min-width: 0;
  font-size: 11.5px;
  line-height: 1.4;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.basics.black : theme.colors.gray.c13};
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  text-decoration: ${({ $stale }) => ($stale ? 'line-through' : 'none')};
  text-decoration-thickness: 1px;

  span {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;

const RoundMeta = styled('span')`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.gray.c10};
`;

const ReplaceNote = styled('div')`
  flex-shrink: 0;
  margin: 2px 10px 8px;
  padding: 8px 9px;
  background: #fff6ed;
  border-left: 2px solid #ffae63;
  border-radius: 0 6px 6px 0;
  font-size: 10.5px;
  line-height: 1.45;
  color: #8a5a22;
`;

const ChainFoot = styled(Stack)`
  flex-shrink: 0;
  position: relative;
  z-index: 1;
  background: ${({ theme }) => theme.colors.basics.white};
  border-top: 1px solid ${({ theme }) => theme.colors.gray.c2};
  padding: 11px 12px 13px;
  min-width: 0;
`;

const FiltLabel = styled('div')`
  font-size: 9.5px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray.c10};
`;

const Chip = styled('button')`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  text-align: left;
  background: ${({ theme }) => theme.colors.basics.white};
  border: 1px solid ${({ theme }) => theme.colors.gray.c4};
  border-radius: 7px;
  padding: 6px 8px;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.blue.primaryA};
    background: ${({ theme }) => theme.colors.blue.c9};
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

const ChipLabel = styled('b')`
  font-weight: 600;
  flex: 0 0 auto;
  font-size: 10.5px;
  color: ${({ theme }) => theme.colors.gray.c11};
`;

const ChipValue = styled('span')`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 10.5px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.basics.black};
`;

const InputWrap = styled('div')`
  position: relative;
  border: 1px solid ${({ theme }) => theme.colors.gray.c4};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.basics.white};
  padding: 9px 10px 34px;

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.blue.primaryA};
  }
`;

const RefineTextarea = styled('textarea')`
  width: 100%;
  border: none;
  outline: none;
  resize: none;
  font-size: 12px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.basics.black};
  height: 40px;
  background: transparent;
  font-family: inherit;

  &::placeholder {
    color: ${({ theme }) => theme.colors.gray.c11};
  }
`;

const SendButton = styled('button')<{ $active: boolean }>`
  position: absolute;
  right: 8px;
  bottom: 8px;
  width: 26px;
  height: 26px;
  border-radius: 7px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ $active }) => ($active ? 'pointer' : 'default')};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.blue.primaryA : theme.colors.gray.c7};
`;

const CapCount = styled('div')`
  font-size: 9.5px;
  color: ${({ theme }) => theme.colors.gray.c10};
  text-align: right;
`;

const Capped = styled(Stack)`
  background: ${({ theme }) => theme.colors.gray.c1};
  border: 1px solid ${({ theme }) => theme.colors.gray.c3};
  border-radius: 8px;
  padding: 12px;
`;

const CappedHead = styled(Inline)`
  align-items: flex-start;
  gap: 6px;
  font-size: 11.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray.c13};
  line-height: 1.4;
`;

const CappedText = styled('p')`
  margin: 0;
  font-size: 10.5px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.gray.c11};
`;
